<template>
  <div>
    <b-navbar
      toggleable="lg"
      type="dark"
      :variant="navbarVariant"
      class="gid-header"
      ref="mainHeader"
    >
      <back-button class="" />
      <router-link class="text-center" to="/">
        <env-orbit-logo :environment="environment" :radius="8" :duration="16">
          <img src="@/assets/gid-logo.svg" alt="GID" class="gid-header__logo" />
        </env-orbit-logo>
      </router-link>
      <search class="ml-5 gid-header__search" />

      <!-- AI Chat Toggle Button -->
      <b-button
        v-if="hasAiAssistantAccess"
        @click="toggleAiAssistant"
        :variant="showAiAssistant ? 'light' : 'primary'"
        :class="['ai-chat-toggle', { 'ai-chat-active': showAiAssistant }]"
        :aria-label="
          showAiAssistant ? 'Close AI Assistant' : 'Open AI Assistant'
        "
        v-b-tooltip.hover.left="
          showAiAssistant
            ? 'Close AI Assistant (Press Ctrl+/ or Ctrl+ß)'
            : 'Open AI Assistant (Press Ctrl+/ or Ctrl+ß)'
        "
      >
        <BIconChatDots class="ai-chat-icon" />
        <span class="ai-chat-label d-none d-sm-inline ml-2">AI</span>
      </b-button>

      <b-button
        v-if="!updateExists"
        aria-label="Toggle navigation"
        @click="sidebarToggle"
        variant="primary"
        class="gid-navigation-toggler"
      >
        <BIconList />
      </b-button>
      <refresh-button v-if="updateExists" :onRefresh="refreshApp" />
    </b-navbar>
    <div class="gid-mobile-search sticky-top mb-n2 py-2 px-2">
      <search />
    </div>
    <div class="gid-content">
      <div
        :class="[
          'border-right',
          'gid-sidebar',
          { 'gid-sidebar-closed': !sidebarOpen },
        ]"
      >
        <div>
          <button
            class="material-icons ml-auto p-3 border-0 bg-transparent d-block d-lg-none"
            @click="sidebarClose"
          >
            close
          </button>
          <b-navbar-nav class="py-2">
            <b-nav-item
              v-for="(item, index) in navItemsFiltered"
              :key="index"
              :to="item.route"
              link-classes="px-3 d-flex justify-content-between align-items-start"
              @click="sidebarClose"
            >
              <div class="d-flex justify-content-between align-items-start">
                <b-icon
                  v-if="!item.externalIcon"
                  :icon="item.icon"
                  :variant="
                    item.route.name == 'orders-waiting-action' ? 'danger' : ''
                  "
                />
                <component
                  v-else
                  class="bi bi-icon bi-custom-icon"
                  :is="item.icon"
                />
                <span class="ml-2">{{ $t(item.i18nKey) }}</span>
              </div>
              <span
                v-if="!isNullOrUndefined(item.total)"
                class="position-relative badge-light badge-pill border"
              >
                {{ item.total }}
                <span
                  v-if="item.dot"
                  :class="`bg-${item.dot} rounded-circle gid-dot`"
                ></span>
              </span>
            </b-nav-item>
            <!-- Contact Details Box -->
            <div class="mt-5 border-top pt-2">
              <div class="mb-3">
                <b-nav-item
                  href="mailto:partner@getitdone.rocks"
                  link-classes="px-3 d-flex justify-content-between align-items-start"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <b-icon icon="envelope"></b-icon>
                    <span class="ml-2">partner@getitdone.rocks</span>
                  </div>
                </b-nav-item>
              </div>
            </div>

            <b-nav-item
              href="https://www.getitdone.rocks/service-partner-agb"
              target="_blank"
              link-classes="px-3 d-flex justify-content-between align-items-start"
            >
              <div class="d-flex justify-content-between align-items-start">
                <b-icon icon="file-earmark-text" />

                <span class="ml-2">{{ $t('tandc.heading') }}</span>
              </div>
            </b-nav-item>
          </b-navbar-nav>
        </div>
        <AccountDropUp class="gid-sidebar-dropup" @navigate="sidebarClose" />
      </div>
      <b-container fluid style="margin-bottom: 4rem">
        <b-alert
          v-if="notFoundUrl"
          variant="warning"
          dismissible
          show
          class="mt-3"
          @dismissed="clearNotFound"
        >
          {{ $t('not-found-redirect', { url: notFoundUrl }) }}
        </b-alert>
        <slot />
      </b-container>

      <AccountDropUp class="gid-sticky-dropup" @navigate="sidebarClose" />
    </div>
    <Onboarding />
    <ChangeAccountDialog />

    <!-- Global AI Assistant -->
    <llama-chat
      v-if="hasAiAssistantAccess"
      v-show="showAiAssistant"
      position="right"
      :show-action-bar="false"
      @close="showAiAssistant = false"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { getModule } from 'vuex-module-decorators';
import { checkRouteAccess } from '@gid/vue-common/store/auth.module';
import update from '@gid/vue-common/components/mixins/update';
import { COUNTS_REFRESH } from '@/store/sidebar.module';
import { isNullOrUndefined } from '@gid/models/dist/transformers/common';
import { getEnv } from '@gid/vue-common/utils';

import FooterInfo from '@/components/FooterInfo.vue';
import AccountDisplay from '@/components/AccountDisplay.vue';
import ChangeAccountDialog from '@/components/ChangeAccountDialog.vue';
import Search from '@/components/Search.vue';
import { AccountStatusEnum } from '@gid/models/dist/entities/account-status-enum';
import { CrudListActionsEnum } from '@gid/vue-common/store/shared/crud-list.module';
import {
  BIcon,
  BIconArrowClockwise,
  BIconCalendar2Check,
  BIconCalendarX,
  BIconCashStack,
  BIconChatDots,
  BIconCheck2Circle,
  BIconEnvelope,
  BIconExclamationTriangle,
  BIconFileEarmarkText,
  BIconHourglassSplit,
  BIconList,
  BIconPauseCircle,
  BIconStar,
  BIconTelephone,
  BIconTools,
} from 'bootstrap-vue';
import CompanyDocsModule from '../../store/company-docs.module';
import SignatureIcon from '../../assets/icons/signature-icon.svg?component';
import Onboarding from '@/components/onboarding/Onboarding.vue';
import AccountDropUp from '@/components/AccountDropUp.vue';
import LlamaChat from '@gid/vue-common/components/chat-view/llama-chat.vue';
import EnvOrbitLogo from '@gid/vue-common/components/EnvOrbitLogo.vue';

export const NAV_ITEMS = [
  {
    route: { name: 'offers' },
    i18nKey: 'new-offers',
    icon: 'star',
  },
  {
    route: { name: 'orders-waiting' },
    i18nKey: 'waiting-orders',
    icon: 'pause-circle',
  },
  {
    route: { name: 'orders-waiting-action' },
    i18nKey: 'waiting-action-orders',
    icon: 'pause-circle',
  },
  {
    route: { name: 'orders', params: { type: 'open' } },
    i18nKey: 'open-orders',
    icon: 'calendar-x',
  },
  {
    route: { name: 'orders', params: { type: 'scheduled' } },
    i18nKey: 'scheduled-orders',
    icon: 'calendar2-check',
  },
  {
    route: { name: 'orders', params: { type: 'at-work' } },
    i18nKey: 'at-work-orders',
    icon: 'tools',
  },
  {
    route: {
      name: 'orders-final-confirmation',
    },
    i18nKey: 'final-confirmation-orders',
    icon: 'hourglass-split',
  },
  {
    route: { name: 'orders-completed' },
    i18nKey: 'completed-orders',
    icon: 'check2-circle',
  },
  {
    route: { name: 'invoices' },
    i18nKey: 'invoices.name',
    icon: 'cash-stack',
  },
  {
    route: {
      name: 'orders-customer-signature',
    },
    i18nKey: 'customer-signature-orders',
    icon: 'signature-icon',
    externalIcon: true,
  },
];

export default {
  mixins: [update],
  components: {
    AccountDisplay,
    AccountDropUp,
    BIcon,
    BIconArrowClockwise, // eslint-disable-line
    BIconCalendar2Check, // eslint-disable-line
    BIconCalendarX, // eslint-disable-line
    BIconCashStack, // eslint-disable-line
    BIconChatDots,
    BIconCheck2Circle, // eslint-disable-line
    BIconEnvelope, // eslint-disable-line
    BIconExclamationTriangle,
    BIconFileEarmarkText, // eslint-disable-line
    BIconHourglassSplit, // eslint-disable-line
    BIconList,
    BIconPauseCircle, // eslint-disable-line
    BIconStar, // eslint-disable-line
    BIconTelephone, // eslint-disable-line
    BIconTools, // eslint-disable-line
    ChangeAccountDialog,
    FooterInfo,
    LlamaChat,
    Onboarding,
    Search,
    SignatureIcon,
    EnvOrbitLogo,
  },
  data() {
    return {
      navItems: JSON.parse(JSON.stringify(NAV_ITEMS)),
      sidebarOpen: false,
      sidebarRefreshTimer: null,
      headerObserver: null,
      showAiAssistant: false,
    };
  },
  computed: {
    ...mapState({
      accounts: (state) => state.auth.accounts,
      counts: (state) => state.sidebar.counts,
    }),
    ...mapGetters(['environment', 'user', 'isAuthenticated']),
    companyDocsStore() {
      return getModule(CompanyDocsModule, this.$store);
    },
    navbarVariant() {
      if (this.user?.profiles?.includes('readonly')) {
        return 'readonly';
      } else if (this.environment === 'production') {
        return 'light';
      } else {
        return 'staging';
      }
    },
    navItemsFiltered() {
      const navItems = this.navItems.filter((item) => {
        // if (
        //   !this.counts.waiting.total &&
        //   item.route.name === 'orders-waiting'
        // ) {
        //   return false;
        // }
        // if (
        //   !this.counts['waiting-action']?.total &&
        //   item.route.name === 'orders-waiting-action'
        // ) {
        //   return false;
        // }
        const resolved = this.$router.resolve(item.route);
        if (resolved) {
          return checkRouteAccess({
            route: resolved.route,
            user: this.user,
            isAuthenticated: this.isAuthenticated,
          });
        } else {
          return false;
        }
      });
      navItems.forEach((item) => {
        if (item.route.name === 'offers') {
          item.total = this.counts.offers.total;
          if (this.counts.offers.total > 0) {
            item.dot = 'primary';
          }
        } else if (item.route.name === 'orders') {
          item.total = this.counts[item.route.params.type].total;
          if (this.counts[item.route.params.type].danger > 0) {
            item.dot = 'danger';
          } else if (this.counts[item.route.params.type].warning > 0) {
            item.dot = 'warning';
          }
        } else if (item.route.name === 'orders-final-confirmation') {
          item.total = this.counts['final-confirmation'].total;
          if (item.total) {
            item.dot = 'danger';
          }
        } else if (item.route.name === 'orders-waiting') {
          item.total = this.counts.waiting.total;
        } else if (item.route.name === 'orders-waiting-action') {
          item.total = this.counts?.['waiting-action']?.total;
          if (item.total > 0) {
            item.dot = 'danger';
          }
        }
      });
      return navItems;
    },
    notFoundUrl() {
      return this.$route.query.notFound || null;
    },
    hasAiAssistantAccess() {
      const isChatEnabled =
        getEnv('VUE_APP_ENABLE_CHAT_PARTNER') === 'true' ||
        getEnv('VUE_APP_ENABLE_CHAT_PARTNER') === '1';
      if (!isChatEnabled) {
        return false;
      }
      return this.isAuthenticated;
    },
  },
  methods: {
    sidebarToggle() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    sidebarClose() {
      this.sidebarOpen = false;
    },
    isNullOrUndefined,
    clearNotFound() {
      if (this.$route.query.notFound) {
        const query = { ...this.$route.query };
        delete query.notFound;
        this.$router.replace({ query });
      }
    },
    initHeaderResizeObserver() {
      const header = this.$refs.mainHeader;

      this.headerObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.target === header.$el) {
            document.documentElement.style.setProperty(
              '--header-height',
              `${entry.borderBoxSize[0].blockSize}px`,
            );
          }
        }
      });

      this.headerObserver.observe(header.$el);
    },
    toggleAiAssistant() {
      this.showAiAssistant = !this.showAiAssistant;
    },
    handleKeyboardShortcuts(event) {
      // Check if user has AI assistant access
      if (!this.hasAiAssistantAccess) {
        return;
      }

      // Check if the pressed key is "/" or "ß"
      const isShortcutKey = event.key === '/' || event.key === 'ß';
      const isCtrlShortcut = (event.ctrlKey || event.metaKey) && isShortcutKey;

      if (!isShortcutKey) {
        return;
      }

      // Get the currently focused element
      const activeElement = document.activeElement;
      const isInInputField =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.isContentEditable ||
          activeElement.closest('[contenteditable="true"]'));

      // Ctrl+/ or Ctrl+ß works everywhere, even in input fields
      if (isCtrlShortcut) {
        const isInAssistant =
          activeElement && activeElement.closest('.llama-chat-container');
        const isInAssistantTextarea =
          isInAssistant && activeElement.tagName === 'TEXTAREA';

        // Toggle assistant unless typing in the assistant's textarea
        if (!isInAssistantTextarea) {
          event.preventDefault();
          this.showAiAssistant = !this.showAiAssistant;
        }
        return;
      }

      // Regular / or ß (without Ctrl) only works when NOT in input fields
      // If AI assistant is closed, open it (unless cursor is in input field)
      if (!this.showAiAssistant && !isInInputField) {
        event.preventDefault(); // Prevent the character from being typed
        this.showAiAssistant = true;
        return;
      }

      // If AI assistant is open, close it (unless cursor is in textarea within the assistant)
      if (this.showAiAssistant) {
        // Check if the active element is within the AI assistant
        const isInAssistant =
          activeElement && activeElement.closest('.llama-chat-container');
        const isInAssistantTextarea =
          isInAssistant && activeElement.tagName === 'TEXTAREA';

        // Only close if not typing in the assistant's textarea
        if (!isInAssistantTextarea) {
          event.preventDefault(); // Prevent the character from being typed
          this.showAiAssistant = false;
        }
      }
    },
  },
  created() {
    if (this.user?.account?.account_status == AccountStatusEnum.NEW) {
      this.$router.push({ name: 'offers' });
    }
    this.$store.dispatch(COUNTS_REFRESH);
    this.companyDocsStore[CrudListActionsEnum.ITEMS_FETCH]({ query: {} });
  },
  mounted() {
    this.sidebarRefreshTimer = setInterval(() => {
      if (document.visibilityState == 'visible') {
        this.$store.dispatch(COUNTS_REFRESH);
      }
    }, 1000 * 60 * 6);
    this.initHeaderResizeObserver();
    // Add global keyboard shortcuts for AI assistant
    document.addEventListener('keydown', this.handleKeyboardShortcuts);
  },
  beforeDestroy() {
    this.headerObserver?.disconnect();
    // Clean up event listener
    document.removeEventListener('keydown', this.handleKeyboardShortcuts);
  },
  destroyed() {
    clearInterval(this.sidebarRefreshTimer);
  },
};
</script>

<style lang="scss">
@import '@gid/vue-common/scss/_variables.scss';
@import '~bootstrap/scss/functions';
@import '~bootstrap/scss/variables';
@import '~bootstrap/scss/mixins/breakpoints';

:root {
  --header-height: 55px;
}

.gid-sidebar {
  position: fixed;
  z-index: $zindex-fixed + 1;
  left: 0;
  top: 0;
  bottom: 0;
  background: $white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
  margin-top: var(--header-height);

  a:not(.router-link-active) {
    color: inherit;
    &:hover {
      color: $primary;
    }
  }

  @include media-breakpoint-down(md) {
    width: 100%;
    transition: 0.3s transform;
    margin-top: 0;

    &-closed {
      transform: translateX(-100%);
    }

    .gid-sidebar-dropup {
      display: none;
    }
  }

  @include media-breakpoint-up(lg) {
    width: 240px;
  }

  .nav-link {
    position: relative;

    &.router-link-active {
      background: $gray-200;
    }
    .badge-pill {
      line-height: 1.5rem;
      font-size: 0.9em;
    }
    .gid-dot {
      position: absolute;
      top: 0rem;
      right: -0.3rem;
      width: 0.7rem;
      height: 0.7rem;
    }
  }
}

.gid-header {
  &.navbar {
    position: sticky;
    top: 0;
    flex-wrap: nowrap;
    z-index: 1032;
    padding: 0.7rem 1rem;
    gap: 0.5rem 1rem;
  }

  .gid-navigation-toggler {
    padding: 0.1rem 0.5rem;
    font-size: 1.25rem;
    display: none;

    @include media-breakpoint-down(md) {
      display: block;
    }
  }

  &__logo {
    max-width: 12rem;
    width: 100%;
    height: auto;
  }

  &__search {
    @include media-breakpoint-down(md) {
      display: none;
    }
  }
}

.bi-custom-icon {
  max-width: 1.25rem;
  width: 100%;
  height: 1.25rem;
}

.gid-mobile-search {
  display: none;
  background-color: white;

  &.sticky-top {
    top: calc(var(--header-height));
  }

  @include media-breakpoint-down(md) {
    display: block;
  }
}

.gid-footer-info {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: $zindex-fixed;
  gap: 0.5rem;
}

.gid-content {
  .gid-sticky-dropup {
    display: none;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0 auto;
    width: 100%;
  }

  .gid-terms-note {
    right: 13.5rem;
  }

  @include media-breakpoint-up(lg) {
    padding-left: 240px;
  }
  @include media-breakpoint-down(md) {
    .gid-sticky-dropup {
      display: flex;
    }

    & {
      padding-bottom: 3.7rem;
    }

    .gid-footer-info,
    .gid-footer-info.d-flex {
      display: none;
    }
  }
}

// AI Chat Toggle Button Styles
.ai-chat-toggle {
  position: relative;
  padding: 0.5rem 0.75rem;
  font-size: 1.25rem;
  min-width: 44px; // Touch-friendly minimum size
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 2px solid transparent;

  // Subtle pulse animation when inactive to draw attention
  &:not(.ai-chat-active) {
    animation: ai-pulse 2s ease-in-out infinite;
  }

  // Active state styling
  &.ai-chat-active {
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);

    .ai-chat-icon {
      animation: ai-bounce 0.5s ease;
    }
  }

  // Icon animation
  .ai-chat-icon {
    font-size: 1.4rem;
    transition: transform 0.3s ease;
  }

  // Hover effect
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);

    .ai-chat-icon {
      transform: scale(1.1);
    }
  }

  // Active press effect
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  // Label (hidden on small screens)
  .ai-chat-label {
    font-weight: 600;
    font-size: 0.875rem;
    letter-spacing: 0.5px;
  }

  // Mobile adjustments
  @include media-breakpoint-down(sm) {
    padding: 0.5rem;
    font-size: 1.5rem;

    .ai-chat-icon {
      font-size: 1.5rem;
    }
  }
}

// Keyframe animations
@keyframes ai-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(var(--primary-rgb, 0, 123, 255), 0);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(var(--primary-rgb, 0, 123, 255), 0.2);
  }
}

@keyframes ai-bounce {
  0%,
  100% {
    transform: scale(1);
  }
  25% {
    transform: scale(0.9);
  }
  50% {
    transform: scale(1.1);
  }
  75% {
    transform: scale(0.95);
  }
}

// Notification badge (for future use)
.ai-chat-toggle .badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 2px 4px;
  font-size: 0.65rem;
  border-radius: 9px;
  background: $danger;
  color: white;
  border: 2px solid currentColor;
}
</style>
